import { useCallback, useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';

import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import useAuth from '../../Hooks/useAuth';
import { H3 } from '../../AbstractElements';
import { CustomTable } from '../../components/common/Table/CustomTable';
import { InvestmentMapper } from '../../infrastructure/mappers/investment.mapper';
import { tableColumnsInvestment } from './Components/TableColumns';

import '../panel/panel.scss';
import './investment.scss';

export const Investment = () => {
   const { user } = useAuth();
   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

   // State for dynamic KPI stats calculated from currently loaded page data
   const [stats, setStats] = useState({
      totalCount: 0,
      totalInvested: 0,
      totalWithdrawn: 0,
      averageRate: 0,
   });

   useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   const handleDataLoad = useCallback((data: { data: any[]; total: number }) => {
      let investedSum = 0;
      let withdrawnSum = 0;
      let rateSum = 0;
      let investmentCount = 0;
      if (data && data.data) {
         data.data.forEach((item) => {
            const val = Number(item.value || 0);
            const rate = Number(item.rate || 0);
            const type = item.type; // 'INVESTMENT' or 'WITHDRAWAL'

            if (type === 'INVESTMENT') {
               investedSum += val;
               rateSum += rate;
               investmentCount++;
            } else if (type === 'WITHDRAWAL') {
               withdrawnSum += val;
            }
         });
      }
      setStats({
         totalCount: data?.total || 0,
         totalInvested: investedSum,
         totalWithdrawn: withdrawnSum,
         averageRate: investmentCount > 0 ? rateSum / investmentCount : 0,
      });
   }, []);

   const customTableStyles = {
      table: {
         style: {
            minHeight: '400px',
         },
      },
      headRow: {
         style: {
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            borderBottom: '2px solid #dee2e6',
            minHeight: '52px',
         },
      },
      headCells: {
         style: {
            fontSize: '13px',
            fontWeight: '600',
            color: '#495057',
            textTransform: 'uppercase' as const,
         },
      },
      rows: {
         style: {
            minHeight: '56px',
            '&:not(:last-of-type)': {
               borderBottom: '1px solid #e9ecef',
            },
            '&:hover': {
               backgroundColor: '#f8f9fa',
               transition: 'all 0.2s ease',
            },
         },
      },
      cells: {
         style: {
            fontSize: '14px',
         },
      },
   };

   return (
      <div className="page-body fluides-dashboard">
         <Breadcrumbs
            pageTitle="Inversiones"
            parent="Sociedad"
            title="Inversiones"
         />
         <Container className="general-widget pb-5" fluid>
            {/* Metrics KPI widgets */}
            <Row className="mb-3 kpi-widgets-row">
               {/* KPI 1: Inversiones Registradas */}
               <Col xl={3} sm={6} className="mb-3 mb-xl-0">
                  <Card className="kpi-card shadow-sm hover-lift bg-white animate-fade-in-up border-0 h-100">
                     <CardBody className="d-flex align-items-center gap-3 p-3">
                        <div className="kpi-icon-container primary">
                           <i className="fa fa-piggy-bank fa-2x"></i>
                        </div>
                        <div>
                           <h4 className="kpi-value mb-0">{stats.totalCount}</h4>
                           <span className="kpi-title text-muted font-weight-medium">
                              <span className="d-none d-md-inline">Inversiones Registradas</span>
                              <span className="d-inline d-md-none">Registradas</span>
                           </span>
                        </div>
                     </CardBody>
                  </Card>
               </Col>

               {/* KPI 2: Total Inversiones */}
               <Col xl={3} sm={6} className="mb-3 mb-xl-0">
                  <Card className="kpi-card shadow-sm hover-lift bg-white animate-fade-in-up delay-1 border-0 h-100">
                     <CardBody className="d-flex align-items-center gap-3 p-3">
                        <div className="kpi-icon-container success">
                           <i className="fa fa-dollar-sign fa-2x"></i>
                        </div>
                        <div>
                           <h4 className="kpi-value mb-0">
                              {stats.totalInvested.toLocaleString('es-CO', {
                                 style: 'currency',
                                 currency: 'COP',
                                 minimumFractionDigits: 0,
                              })}
                           </h4>
                           <span className="kpi-title text-muted font-weight-medium">
                              <span className="d-none d-md-inline">Total Inversiones (Pág. Actual)</span>
                              <span className="d-inline d-md-none">Total Inv.</span>
                           </span>
                        </div>
                     </CardBody>
                  </Card>
               </Col>

               {/* KPI 3: Total Retiros */}
               <Col xl={3} sm={6} className="mb-3 mb-sm-0">
                  <Card className="kpi-card shadow-sm hover-lift bg-white animate-fade-in-up delay-2 border-0 h-100">
                     <CardBody className="d-flex align-items-center gap-3 p-3">
                        <div className="kpi-icon-container secondary">
                           <i className="fa fa-arrow-down fa-2x"></i>
                        </div>
                        <div>
                           <h4 className="kpi-value mb-0">
                              {stats.totalWithdrawn.toLocaleString('es-CO', {
                                 style: 'currency',
                                 currency: 'COP',
                                 minimumFractionDigits: 0,
                              })}
                           </h4>
                           <span className="kpi-title text-muted font-weight-medium">
                              <span className="d-none d-md-inline">Total Retiros (Pág. Actual)</span>
                              <span className="d-inline d-md-none">Retiros</span>
                           </span>
                        </div>
                     </CardBody>
                  </Card>
               </Col>

               {/* KPI 4: Tasa Promedio */}
               <Col xl={3} sm={6}>
                  <Card className="kpi-card shadow-sm hover-lift bg-white animate-fade-in-up delay-3 border-0 h-100">
                     <CardBody className="d-flex align-items-center gap-3 p-3">
                        <div className="kpi-icon-container primary">
                           <i className="fa fa-percentage fa-2x"></i>
                        </div>
                        <div>
                           <h4 className="kpi-value mb-0">
                              {stats.averageRate.toLocaleString('es-CO', {
                                 minimumFractionDigits: 2,
                                 maximumFractionDigits: 2,
                              })}%
                           </h4>
                           <span className="kpi-title text-muted font-weight-medium">
                              <span className="d-none d-md-inline">Tasa Promedio (Pág. Actual)</span>
                              <span className="d-inline d-md-none">Tasa Prom.</span>
                           </span>
                        </div>
                     </CardBody>
                  </Card>
               </Col>
            </Row>

            <Row className="animate-fade-in-up delay-4">
               <Col xs={12} className="px-0">
                  <Card className="premium-card border-0 h-100 shadow-sm" style={{ borderRadius: '16px' }}>
                     <CardHeader className="card-no-border pb-0 bg-transparent">
                        <div className="header-top">
                           <H3 className="mb-0 fw-bold fs-5 text-dark">Listado de Inversiones</H3>
                        </div>
                     </CardHeader>

                     <CardBody className="p-4 pt-3">
                        <div className="table-responsive">
                           <CustomTable
                              route={`investments/${user?.id}`}
                              columns={tableColumnsInvestment}
                              mapper={InvestmentMapper.mapResponseToInvestment}
                              customStyles={customTableStyles}
                              showSearch
                              enableColumnToggle={true}
                              enableCompactMode={true}
                              enableAdvancedFilter={true}
                              onDataLoad={handleDataLoad}
                           />
                        </div>
                     </CardBody>
                  </Card>
               </Col>
            </Row>
         </Container>
      </div>
   );
};

export default Investment;


